import { Link, useParams, useSearchParams } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/feature/Pagination";
import FilterSidebar from "@/pages/category/components/FilterSidebar";
import type { ContentType } from "@/types/movie";
import {
  getAllGenres,
  getMoviesByType,
  matchYearRange,
  sortByPopular,
  sortByUpdated,
  typeLabel,
} from "@/lib/movieData";

const PAGE_SIZE = 8;

export default function Category() {
  const { type } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const isValid = type === "movie" || type === "tv";
  const contentType = (type === "tv" ? "tv" : "movie") as ContentType;
  const label = typeLabel(contentType);

  const genre = searchParams.get("genre") || "all";
  const year = searchParams.get("year") || "all";
  const sort = searchParams.get("sort") || "updated";
  const page = Math.max(1, Number(searchParams.get("page") || 1));

  usePageMeta({
    title: `${label} - 最近中文字幕`,
    description: `最近中文字幕${label}专区，汇集最新带中文字幕的${label}作品，提供题材、年份筛选与字幕更新信息，帮助中文观众快速找到想看的内容。`,
    keywords: `最近中文字幕,中文字幕${label},最新${label},${label}字幕,影视资源`,
  });

  const updateParams = (updates: Record<string, string>, resetPage = true) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      const isDefault =
        (key === "genre" && value === "all") ||
        (key === "year" && value === "all") ||
        (key === "sort" && value === "updated");
      if (isDefault) next.delete(key);
      else next.set(key, value);
    });
    if (resetPage) next.delete("page");
    setSearchParams(next);
  };

  if (!isValid) {
    return (
      <div className="w-full px-4 md:px-6 py-24 text-center">
        <i className="ri-error-warning-line text-5xl text-foreground-300"></i>
        <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">
          分类不存在
        </h1>
        <p className="mt-2 text-sm text-foreground-500">请从下方入口进入正确的分类专区。</p>
        <div className="mt-6 flex justify-center gap-3">
          <Link
            to="/category/movie"
            className="rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 dark:text-foreground-950 whitespace-nowrap"
          >
            电影专区
          </Link>
          <Link
            to="/category/tv"
            className="rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-700 whitespace-nowrap"
          >
            电视剧专区
          </Link>
        </div>
      </div>
    );
  }

  const base = getMoviesByType(contentType);
  const genres = getAllGenres(contentType);

  let list = base.filter((movie) => {
    const genreOk = genre === "all" || movie.genres.includes(genre);
    const yearOk = matchYearRange(movie.year, year);
    return genreOk && yearOk;
  });

  if (sort === "views") list = sortByPopular(list);
  else if (sort === "rating") list = [...list].sort((a, b) => b.rating - a.rating);
  else list = sortByUpdated(list);

  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = list.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="w-full px-4 md:px-6 py-8 md:py-10">
      <nav className="mb-4 flex items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="hover:text-primary-700">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-900">{label}</span>
      </nav>

      <header className="mb-6 md:mb-8">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          {label}专区 · 最新中文字幕{label}
        </h1>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
          汇集最近更新的带中文字幕{label}作品，可按题材与年份筛选，并查看每部作品的字幕更新情况。
        </p>
      </header>

      <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
        <FilterSidebar
          genres={genres}
          activeGenre={genre}
          activeYear={year}
          sort={sort}
          onGenreChange={(value) => updateParams({ genre: value })}
          onYearChange={(value) => updateParams({ year: value })}
          onSortChange={(value) => updateParams({ sort: value })}
          onReset={() => updateParams({ genre: "all", year: "all", sort: "updated" })}
        />

        <div className="flex-1 min-w-0">
          <div className="mb-4 flex items-center justify-between text-sm text-foreground-500">
            <span>
              共找到 <strong className="text-foreground-900">{list.length}</strong> 部作品
            </span>
            <span className="hidden sm:block">第 {currentPage} / {totalPages} 页</span>
          </div>

          {paged.length === 0 ? (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
              <i className="ri-inbox-2-line text-5xl text-foreground-300"></i>
              <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                没有找到符合条件的作品
              </h2>
              <p className="mt-2 text-sm text-foreground-500">
                试试更换题材或年份条件，或直接查看全部内容。
              </p>
              <button
                type="button"
                onClick={() => updateParams({ genre: "all", year: "all", sort: "updated" })}
                className="mt-6 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950 whitespace-nowrap"
              >
                清空筛选条件
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 xl:grid-cols-4">
              {paged.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          )}

          <Pagination
            page={currentPage}
            totalPages={totalPages}
            onChange={(next) => updateParams({ page: String(next) }, false)}
          />
        </div>
      </div>
    </div>
  );
}